import { config } from 'src/utils/config';

/**
 * Storyboard frames are separate stored images, numbered from 1:
 * `storyboard_000001.jpg` ... (backend `build_storyboard_request`,
 * file_prefix "storyboard_"). The asset's `storyboard` conversion carries the
 * count as `file_count`.
 */
export const storyboardFrameName = (frame: number): string =>
  `storyboard_${String(frame).padStart(6, '0')}.jpg`;

/**
 * A frame through the on-demand imaging route, resized at the edge and
 * CDN-cached: GET /api/v1/assets/{id}/storyboard/{frame}?resize=w,h.
 * Same `_ctx=ui` / `_jwt` handling as `getThumbnailUrl` (previews.ts).
 */
export const storyboardFrameUrl = ({
  assetId,
  frame,
  width,
  height,
  jwt,
}: {
  assetId: string;
  frame: number;
  width: number;
  height: number;
  jwt?: string;
}): string => {
  const base = `${config.cdnUrl}/api/v1/assets/${assetId}/storyboard/${storyboardFrameName(frame)}?resize=${width},${height}&_ctx=ui`;
  return jwt ? `${base}&_jwt=${jwt.replace('Bearer ', '')}` : base;
};

/**
 * Up to `max` frame numbers spread evenly from the first frame to the last.
 * The first ten frames of a one-every-5s storyboard cover only 50 seconds;
 * an even spread previews the whole video.
 */
export const sampleStoryboardFrames = (count: number, max = 10): number[] => {
  if (count <= 0) return [];
  if (count <= max) return Array.from({ length: count }, (_, i) => i + 1);
  if (max === 1) return [1];
  const frames = new Set<number>();
  for (let i = 0; i < max; i += 1) {
    frames.add(1 + Math.round((i * (count - 1)) / (max - 1)));
  }
  return Array.from(frames);
};

/**
 * Roughly where a frame sits in the video, in seconds — or null when it can't
 * be told.
 *
 * Frames are evenly spaced in both "every N seconds" and "N frames over the
 * video" modes, so (frame - 1) × duration / count holds for either, and it
 * doesn't depend on the CURRENT settings matching the ones the storyboard was
 * made with. "keyframes" mode spaces frames irregularly, so no time then.
 */
export const approxFrameSeconds = (
  frame: number,
  count: number,
  durationSeconds: number | undefined,
  mode: string | undefined,
): number | null => {
  if (mode === 'keyframes' || !durationSeconds || durationSeconds <= 0 || count <= 0) return null;
  return Math.round(((frame - 1) * durationSeconds) / count);
};

/** 75 -> "1:15", 3725 -> "1:02:05". */
export const formatFrameTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};
